import type { MaybeRefOrGetter } from 'vue';
import type { BlockDefinition, BlockSerialized } from '#layers/block-engine/public';
import type { ResolvedMonospacePrimitiveField, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import { computed, toValue } from 'vue';
import { useNuxtApp } from '#app/nuxt';
import { callOrReturn, omit } from '#layers/base/public';
import { getBlockConfig, isBlockDefinition } from '#layers/block-engine/public';
import { getFieldDisplayBlockOptions, getFieldTypeOptionNames, useFieldTypeTemplates } from '#layers/data-model/public';

export interface FieldDisplayBlockResolver {
	getBlock: (id: string) => BlockSerialized | BlockDefinition | undefined;
	isReady: () => boolean;
}

declare module '#app' {
	interface NuxtApp {
		$fieldDisplayBlockResolver: FieldDisplayBlockResolver;
	}
}

export function usePrimitiveFieldDisplay(field: MaybeRefOrGetter<ResolvedMonospacePrimitiveField>) {
	const computedField = computed(() => toValue(field));
	const { getBlock, isReady } = useNuxtApp().$fieldDisplayBlockResolver;
	const fieldTypeTemplates = useFieldTypeTemplates();

	const base = computed(() => getFieldDisplayBlockOptions(computedField.value));

	const blockDef = computed(() => {
		const block = getBlock(base.value.block);
		if (!isBlockDefinition(block)) return;
		return block;
	});

	// True until the shared block library has finished its first fetch. Prevents
	// callers from briefly rendering the empty-state fallback while the lookup
	// is still pending (e.g. first navigation to a layout before cache warm-up).
	const isResolving = computed(() => !isReady() && !blockDef.value);

	const defaultProps = computed(() => {
		if (!blockDef.value) return null;

		const blockConfig = getBlockConfig(blockDef.value);

		return callOrReturn(blockConfig?.fieldBinding?.defaultProps, {
			primitiveType: computedField.value.type,
			fieldTypeId: computedField.value.meta?.typeTemplateId ?? '',
			typeOptions: computedField.value.meta?.typeOptions ?? {},
		});
	});
	const inheritedOptionNames = computed(() => {
		if (Object.keys(computedField.value.meta?.displayOptions ?? {}).length === 0) return [];

		const fieldTypeId = computedField.value.meta?.typeTemplateId;
		const fieldType = fieldTypeId ? fieldTypeTemplates.value[fieldTypeId] : undefined;
		const collectionId = computedField.value.collectionId;

		if (!fieldType || !collectionId) return [];

		return getFieldTypeOptionNames({
			fieldType,
			primitiveType: computedField.value.type,
			typeOptions: computedField.value.meta?.typeOptions,
			collectionId,
		});
	});
	const displayProps = computed(() => inheritedOptionNames.value.length === 0
		? base.value.props
		: omit(base.value.props, inheritedOptionNames.value as Array<keyof typeof base.value.props>));

	return {
		block: computed(() => blockDef.value?.id),
		props: computed(() => ({
			primitiveType: computedField.value.type,
			...defaultProps.value,
			...computedField.value.meta?.typeOptions,
			...displayProps.value,
		})),
		isResolving,
	};
}

export function useRelationFieldDisplay(field: MaybeRefOrGetter<ResolvedMonospaceSingleRelationField>) {
	const computedField = computed(() => toValue(field));
	const base = computed(() => getFieldDisplayBlockOptions(computedField.value));

	return {
		block: computed(() => base.value.block),
		props: computed(() => base.value.props),
	};
}
